/**
 * Operator Goals Hooks
 *
 * TanStack Query hooks for operator goal CRUD operations.
 * Follows the same patterns as useOperatorPortal.ts.
 *
 * - useOperatorGoals(status?) — list goals, optionally filtered by status
 * - useCreateGoal() — create a new goal
 * - useUpdateGoal() — update target or abandon a goal
 * - useDeleteGoal() — hard-delete a goal
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/hooks/useApi';
import type { OperatorGoalResponse, CreateOperatorGoal, UpdateOperatorGoal, GoalStatus } from '@guild/shared';

// ============================================
// TYPES (inline — history view types are read-only and don't need shared package)
// ============================================

export interface GoalAssessmentHistoryItem {
  id: string;
  assessedAt: string;
  overallScore: number;
  complianceScore: number;
  efficiencyScore: number;
  safetyScore: number;
  relevantScore: number;
  sectionTitle: string | null;
  delta: number | null;
}

export interface GoalHistoryData {
  goal: OperatorGoalResponse;
  history: GoalAssessmentHistoryItem[];
}

// ============================================
// QUERY KEYS
// ============================================

export const operatorGoalKeys = {
  all: ['operator-goals'] as const,
  list: (status?: string) => [...operatorGoalKeys.all, 'list', status ?? 'all'] as const,
  history: (goalId: string) => [...operatorGoalKeys.all, 'history', goalId] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch operator goals, optionally filtered by status.
 */
export function useOperatorGoals(status?: GoalStatus) {
  const api = useApi();
  const params = status ? `?status=${status}` : '';

  return useQuery({
    queryKey: operatorGoalKeys.list(status),
    queryFn: () => api.get<OperatorGoalResponse[]>(`/api/operator/goals${params}`),
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Create a new operator goal.
 * Returns OperatorGoalResponse (with computed fields: currentScore, trend, sopTitle).
 */
export function useCreateGoal() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOperatorGoal) =>
      api.post<OperatorGoalResponse>('/api/operator/goals', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatorGoalKeys.all });
    },
  });
}

/**
 * Update an existing operator goal (change target score or abandon).
 */
export function useUpdateGoal() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: UpdateOperatorGoal }) =>
      api.patch<OperatorGoalResponse>(`/api/operator/goals/${goalId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatorGoalKeys.all });
    },
  });
}

/**
 * Hard-delete an operator goal.
 */
export function useDeleteGoal() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goalId: string) =>
      api.delete<void>(`/api/operator/goals/${goalId}`),
    onSuccess: (_data, goalId) => {
      // Remove the history query for this goal before invalidating the list.
      // Without this, TanStack Query would refetch the history for the deleted
      // goal as part of the invalidation sweep, producing a 404 (race condition).
      queryClient.removeQueries({ queryKey: operatorGoalKeys.history(goalId) });
      queryClient.invalidateQueries({ queryKey: operatorGoalKeys.all });
    },
  });
}

// ============================================
// HISTORY QUERY (Phase 21.3.4)
// ============================================

/**
 * Fetch assessment history for a specific goal.
 * Only enabled when goalId is provided (detail sheet is open).
 * staleTime: 60s to prevent re-fetch on rapid open/close of the same sheet.
 */
export function useGoalHistory(goalId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: operatorGoalKeys.history(goalId ?? ''),
    queryFn: () => api.get<GoalHistoryData>(`/api/operator/goals/${goalId!}/history`),
    enabled: !!goalId,
    staleTime: 60_000,
  });
}
